import React, { useState, useEffect } from 'react';
import { Users, Plus, Edit, Trash2, AlertCircle, CheckCircle, Search, Mail, User as UserIcon, Building } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import AdminGlobalLayout from '../../components/AdminGlobalLayout';
import AdminDataTable from '../../components/AdminDataTable';

interface User {
  id: string;
  email: string;
  user_metadata: {
    name?: string;
    role?: string;
    igreja_id?: string;
  };
  created_at: string;
  igreja_id: string;
  igreja_nome?: string;
}

interface Igreja {
  id: string;
  nome: string;
  cidade: string;
  estado: string;
}

interface UserFormData {
  email: string;
  name: string;
  password: string;
  role: 'member' | 'admin_local' | 'admin_global';
  igreja_id: string;
}

export default function AdminUsers() {
  const [users, setUsers] = useState<User[]>([]);
  const [igrejas, setIgrejas] = useState<Igreja[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    name: '',
    password: '',
    role: 'member',
    igreja_id: ''
  });
  
  // Table state
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string | null>('email');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [selectedIgreja, setSelectedIgreja] = useState<string>('');

  useEffect(() => {
    fetchUsers();
    fetchIgrejas();
  }, []);

  useEffect(() => {
    // Filter and sort users
    let filtered = [...users];
    
    // Apply igreja filter
    if (selectedIgreja) {
      filtered = filtered.filter(user => user.igreja_id === selectedIgreja);
    }
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(user => 
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.user_metadata?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.igreja_nome?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply sorting
    if (sortField && sortOrder) {
      filtered.sort((a, b) => {
        let aValue, bValue;
        
        if (sortField === 'name') {
          aValue = a.user_metadata?.name || '';
          bValue = b.user_metadata?.name || '';
        } else if (sortField === 'role') {
          aValue = a.user_metadata?.role || '';
          bValue = b.user_metadata?.role || '';
        } else if (sortField === 'igreja_nome') {
          aValue = a.igreja_nome || '';
          bValue = b.igreja_nome || '';
        } else {
          aValue = a[sortField as keyof User];
          bValue = b[sortField as keyof User];
        }
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortOrder === 'asc' 
            ? aValue.localeCompare(bValue) 
            : bValue.localeCompare(aValue);
        }
        
        return 0;
      });
    }
    
    setFilteredUsers(filtered);
  }, [users, searchTerm, sortField, sortOrder, selectedIgreja]);

  async function fetchUsers() {
    try {
      setLoading(true);
      
      // Fetch users
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('*');
      
      if (usersError) throw usersError;
      
      // Fetch igrejas to get names
      const { data: igrejasData, error: igrejasError } = await supabase
        .from('igrejas')
        .select('id, nome');
      
      if (igrejasError) throw igrejasError;
      
      // Map igreja names to users
      const usersWithIgrejaNames = usersData?.map(user => {
        const igreja = igrejasData?.find(i => i.id === user.igreja_id);
        return {
          ...user,
          igreja_nome: igreja?.nome || 'Sem igreja'
        };
      }) || [];
      
      setUsers(usersWithIgrejaNames);
    } catch (error: any) {
      setError(error.message || 'Erro ao buscar usuários');
    } finally {
      setLoading(false);
    }
  }

  async function fetchIgrejas() {
    try {
      const { data, error } = await supabase
        .from('igrejas')
        .select('id, nome, cidade, estado')
        .order('nome');

      if (error) throw error;
      setIgrejas(data || []);
    } catch (error: any) {
      setError(error.message || 'Erro ao buscar igrejas');
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    try {
      if (editingUser) {
        // Update existing user
        const updates = {
          user_metadata: {
            ...editingUser.user_metadata,
            name: formData.name,
            role: formData.role
          },
          igreja_id: formData.igreja_id
        };
        
        const { error } = await supabase
          .from('users')
          .update(updates)
          .eq('id', editingUser.id);
        
        if (error) throw error;
        setSuccess('Usuário atualizado com sucesso!');
      } else {
        // Create new user
        const { data, error } = await supabase.auth.signUp({
          email: formData.email,
          password: formData.password,
          options: {
            data: {
              name: formData.name,
              role: formData.role,
              igreja_id: formData.igreja_id
            }
          }
        });
        
        if (error) throw error;
        
        if (data.user) {
          // Also update the users table with igreja_id
          const { error: updateError } = await supabase
            .from('users')
            .update({ igreja_id: formData.igreja_id })
            .eq('id', data.user.id);
          
          if (updateError) throw updateError;
          
          setSuccess('Usuário criado com sucesso!');
        }
      }
      
      // Reset form and close modal
      setFormData({
        email: '',
        name: '',
        password: '',
        role: 'member',
        igreja_id: ''
      });
      setIsModalOpen(false);
      setEditingUser(null);
      
      // Refresh data
      fetchUsers();
    } catch (error: any) {
      setError(error.message || 'Erro ao salvar usuário');
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      name: user.user_metadata?.name || '',
      password: '', // Password field is empty when editing
      role: (user.user_metadata?.role as any) || 'member',
      igreja_id: user.igreja_id || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    
    try {
      setError('');
      setSuccess('');
      
      // Delete user
      const { error } = await supabase.auth.admin.deleteUser(userToDelete.id);
      
      if (error) throw error;
      
      setSuccess('Usuário excluído com sucesso!');
      setIsDeleteModalOpen(false);
      setUserToDelete(null);
      
      // Refresh data
      fetchUsers();
    } catch (error: any) {
      setError(error.message || 'Erro ao excluir usuário');
    }
  };

  const handleSortingChange = (field: string, order: 'asc' | 'desc') => {
    setSortField(field);
    setSortOrder(order);
  };

  const columns = [
    {
      header: 'Nome',
      accessorKey: 'name',
      enableSorting: true,
      cell: (info: { row: User }) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-brand-light text-brand-teal flex items-center justify-center font-medium">
            {info.row.user_metadata?.name?.[0]?.toUpperCase() || info.row.email[0].toUpperCase()}
          </div>
          <div>
            <div className="font-medium text-gray-900">{info.row.user_metadata?.name || 'Sem nome'}</div>
            <div className="text-sm text-gray-500">{info.row.email}</div>
          </div>
        </div>
      )
    },
    {
      header: 'Igreja',
      accessorKey: 'igreja_nome',
      enableSorting: true,
      cell: (info: { row: User }) => (
        <div>{info.row.igreja_nome || 'Sem igreja'}</div>
      )
    },
    {
      header: 'Função',
      accessorKey: 'role',
      enableSorting: true,
      cell: (info: { row: User }) => {
        const role = info.row.user_metadata?.role;
        let badge;
        
        switch (role) {
          case 'admin_global':
            badge = <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">Admin Global</span>;
            break;
          case 'admin_local':
            badge = <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">Admin Local</span>;
            break;
          default:
            badge = <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">Membro</span>;
        }
        
        return badge;
      }
    },
    {
      header: 'Criado em',
      accessorKey: 'created_at',
      enableSorting: true,
      cell: (info: { row: User }) => (
        <div>{new Date(info.row.created_at).toLocaleDateString('pt-BR')}</div>
      )
    },
    {
      header: 'Ações',
      accessorKey: 'id',
      cell: (info: { row: User }) => (
        <div className="flex justify-end space-x-2">
          <button
            onClick={() => handleEdit(info.row)}
            className="p-1 text-brand-teal hover:text-brand-teal/80 rounded-full"
            title="Editar"
          >
            <Edit className="h-5 w-5" />
          </button>
          <button
            onClick={() => {
              setUserToDelete(info.row);
              setIsDeleteModalOpen(true);
            }}
            className="p-1 text-red-500 hover:text-red-600 rounded-full"
            title="Excluir"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        </div>
      )
    }
  ];

  // Calculate pagination
  const pageCount = Math.ceil(filteredUsers.length / pageSize);
  const paginatedData = filteredUsers.slice(
    pageIndex * pageSize,
    (pageIndex + 1) * pageSize
  );

  return (
    <AdminGlobalLayout title="Gerenciar Usuários">
      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg flex items-start">
          <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 text-green-600 rounded-lg flex items-start">
          <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
          <span>{success}</span>
        </div>
      )}

      {/* Igreja Filter */}
      <div className="mb-6">
        <label htmlFor="igreja_filter" className="block text-sm font-medium text-gray-700 mb-1">
          Filtrar por Igreja
        </label>
        <select
          id="igreja_filter"
          value={selectedIgreja}
          onChange={(e) => setSelectedIgreja(e.target.value)}
          className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
        >
          <option value="">Todas as Igrejas</option>
          {igrejas.map((igreja) => (
            <option key={igreja.id} value={igreja.id}>
              {igreja.nome} - {igreja.cidade}/{igreja.estado}
            </option>
          ))}
        </select>
      </div>

      <AdminDataTable
        data={paginatedData}
        columns={columns}
        title="Usuários"
        subtitle={`Total de ${filteredUsers.length} usuários cadastrados`}
        loading={loading}
        pagination={{
          pageIndex,
          pageSize,
          pageCount,
          onPageChange: setPageIndex
        }}
        sorting={{
          field: sortField,
          order: sortOrder,
          onSortingChange: handleSortingChange
        }}
        filtering={{
          globalFilter: searchTerm,
          onGlobalFilterChange: setSearchTerm
        }}
        enableExport={true}
        exportFileName="usuarios"
        actions={
          <button
            onClick={() => {
              setEditingUser(null);
              setFormData({
                email: '',
                name: '',
                password: '',
                role: 'member',
                igreja_id: ''
              });
              setIsModalOpen(true);
            }}
            className="px-3 py-2 bg-brand-teal text-white rounded-lg hover:bg-brand-teal/90 transition-colors flex items-center gap-2 text-sm"
          >
            <Plus className="h-4 w-4" />
            <span>Novo Usuário</span>
          </button>
        }
      />

      {/* User Form Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-brand-teal mb-6">
              {editingUser ? 'Editar Usuário' : 'Novo Usuário'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Nome Completo *
                  </label>
                  <div className="relative">
                    <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                    />
                  </div>
                </div>
                
                <div className="md:col-span-2">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      disabled={!!editingUser}
                      className={`pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal ${
                        editingUser ? 'bg-gray-100 cursor-not-allowed' : ''
                      }`}
                    />
                  </div>
                  {editingUser && (
                    <p className="mt-1 text-xs text-gray-500">O email não pode ser alterado.</p>
                  )}
                </div>
                
                {!editingUser && (
                  <div className="md:col-span-2">
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                      Senha *
                    </label>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required={!editingUser}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                    />
                  </div>
                )}
                
                <div>
                  <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                    Função *
                  </label>
                  <select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                  >
                    <option value="member">Membro</option>
                    <option value="admin_local">Administrador Local</option>
                    <option value="admin_global">Administrador Global</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="igreja_id" className="block text-sm font-medium text-gray-700 mb-1">
                    Igreja *
                  </label>
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <select
                      id="igreja_id"
                      name="igreja_id"
                      value={formData.igreja_id}
                      onChange={handleInputChange}
                      required
                      className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                    >
                      <option value="">Selecione uma igreja...</option>
                      {igrejas.map((igreja) => (
                        <option key={igreja.id} value={igreja.id}>
                          {igreja.nome} - {igreja.cidade}/{igreja.estado}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-brand-teal text-white rounded-lg hover:bg-brand-teal/90 transition-colors"
                >
                  {editingUser ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && userToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirmar exclusão</h3>
            <p className="text-gray-600 mb-6">
              Tem certeza que deseja excluir o usuário <strong>{userToDelete.user_metadata?.name || userToDelete.email}</strong>? Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setUserToDelete(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminGlobalLayout>
  );
}